import React, { useEffect, useState } from "react";
import api from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Badge } from "../../components/ui/badge";
import { Checkbox } from "../../components/ui/checkbox";
import { toast } from "sonner";
import { Plus, Trash2, Pencil } from "lucide-react";

const EMPTY = { nom: "", description: "", image: "", type: "manuelle", regle_tag: "", product_ids: [] };

export default function AdminCollections() {
  const [collections, setCollections] = useState([]);
  const [products, setProducts] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);

  const load = () => api.get("/collections").then((r) => setCollections(r.data));
  useEffect(() => {
    load();
    api.get("/admin/products", { params: { limit: 100 } }).then((r) => setProducts(r.data.items));
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const openNew = () => { setForm(EMPTY); setEditingId(null); setOpen(true); };
  const openEdit = (c) => { setForm({ ...EMPTY, ...c }); setEditingId(c.id); setOpen(true); };

  const toggleProduct = (id) => set("product_ids", form.product_ids.includes(id) ? form.product_ids.filter((x) => x !== id) : [...form.product_ids, id]);

  const save = async () => {
    if (!form.nom.trim()) { toast.error("Le nom est requis"); return; }
    try {
      if (editingId) await api.put(`/admin/collections/${editingId}`, form);
      else await api.post("/admin/collections", form);
      toast.success("Collection enregistrée");
      setOpen(false); load();
    } catch { toast.error("Erreur"); }
  };
  const remove = async (id) => { if (!window.confirm("Supprimer ?")) return; await api.delete(`/admin/collections/${id}`); load(); };

  return (
    <div>
      <div className="mb-6 flex items-center justify-between">
        <h1 className="font-serif text-3xl font-medium text-[#2C1A14]">Collections</h1>
        <Button onClick={openNew} data-testid="add-collection-button" className="rounded-full bg-[#D76E4D] text-white hover:bg-[#C05A3A]"><Plus className="mr-1 h-4 w-4" /> Nouvelle collection</Button>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {collections.map((c) => (
          <div key={c.id} data-testid={`collection-${c.id}`} className="rounded-xl bg-white p-5 ring-1 ring-[#E8D5CE]">
            <div className="flex items-start justify-between">
              <div>
                <h3 className="font-serif text-lg text-[#2C1A14]">{c.nom}</h3>
                <Badge className={`mt-1 border-0 ${c.type === "intelligente" ? "bg-purple-100 text-purple-700" : "bg-blue-100 text-blue-700"}`}>
                  {c.type === "intelligente" ? `Auto · tag=${c.regle_tag}` : `Manuelle · ${c.product_ids?.length || 0} pièces`}
                </Badge>
              </div>
              <div className="flex gap-1">
                <button onClick={() => openEdit(c)} data-testid={`edit-collection-${c.id}`} className="rounded p-2 text-[#6E544A] hover:bg-[#F6E4DE]"><Pencil className="h-4 w-4" /></button>
                <button onClick={() => remove(c.id)} data-testid={`delete-collection-${c.id}`} className="rounded p-2 text-red-500 hover:bg-red-50"><Trash2 className="h-4 w-4" /></button>
              </div>
            </div>
            <p className="mt-2 text-sm text-[#6E544A]">{c.description}</p>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto bg-[#FBF1EE]">
          <DialogHeader><DialogTitle className="font-serif text-2xl text-[#2C1A14]">{editingId ? "Modifier" : "Nouvelle"} collection</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Nom</Label><Input data-testid="collection-nom" value={form.nom} onChange={(e) => set("nom", e.target.value)} className="mt-1 bg-white" /></div>
            <div><Label>Description</Label><Textarea value={form.description} onChange={(e) => set("description", e.target.value)} className="mt-1 bg-white" /></div>
            <div>
              <Label>Type de collection</Label>
              <Select value={form.type} onValueChange={(v) => set("type", v)}>
                <SelectTrigger data-testid="collection-type" className="mt-1 bg-white"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="manuelle">Manuelle (sélection)</SelectItem>
                  <SelectItem value="intelligente">Intelligente (règle par tag)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {form.type === "intelligente" ? (
              <div><Label>Tag de la règle</Label><Input data-testid="collection-regle-tag" value={form.regle_tag} onChange={(e) => set("regle_tag", e.target.value)} placeholder="nouveau" className="mt-1 bg-white" /></div>
            ) : (
              <div>
                <Label>Produits inclus</Label>
                <div className="mt-2 max-h-52 space-y-1 overflow-y-auto rounded-lg bg-white p-3">
                  {products.map((p) => (
                    <label key={p.id} className="flex cursor-pointer items-center gap-2 py-1 text-sm">
                      <Checkbox checked={form.product_ids.includes(p.id)} onCheckedChange={() => toggleProduct(p.id)} />
                      {p.nom}
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="border-[#E8D5CE]">Annuler</Button>
            <Button onClick={save} data-testid="save-collection" className="bg-[#D76E4D] text-white hover:bg-[#C05A3A]">Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
